import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';

export function useSessionStorage<T>(
  key: string,
  initialValue?: T,
  onError?: (errorMessage: string) => void,
): [T, Dispatch<SetStateAction<T>>] {
  const deserializer = useCallback<(value: string) => T>(
    (value) => {
      if (value === 'undefined') {
        return undefined;
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(value);
      } catch (error) {
        onError('Error parsing JSON from sessionStorage');
        return initialValue;
      }

      return parsed as T;
    },
    [initialValue],
  );

  const readValue = useCallback((): T => {
    try {
      const raw = window.sessionStorage.getItem(key);
      return raw ? deserializer(raw) : initialValue;
    } catch (error) {
      onError(`Error reading sessionStorage key “${key}”`);
      return initialValue;
    }
  }, [initialValue, key, deserializer]);

  const [storedValue, setStoredValue] = useState<T>(() => {
    return initialValue;
  });

  const setValue: Dispatch<SetStateAction<T>> = useCallback(
    (value) => {
      try {
        // Resolve updates from storage so separate consumers do not overwrite
        // each other with render-time state.
        const nextValue = value instanceof Function ? value(readValue()) : value;
        window.sessionStorage.setItem(key, JSON.stringify(nextValue));

        setStoredValue(nextValue);

        window.dispatchEvent(new StorageEvent('session-storage', { key }));
      } catch (error) {
        onError(`Error setting sessionStorage key “${key}”`);
      }
    },
    [key, readValue],
  );

  useEffect(() => {
    setStoredValue(readValue());
  }, [readValue]);

  useEffect(() => {
    const handleStorageChange = (event: Event) => {
      if (event instanceof StorageEvent && event.key === key) {
        setStoredValue(readValue());
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('session-storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('session-storage', handleStorageChange);
    };
  }, [key, readValue]);

  // Include the error state in the return value
  return [storedValue, setValue];
}
